import { memo, MutableRefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'cropperjs';

import { SVGIconType } from '../../../constants/svgIcon';
import { SvgIcon } from '../SVGIcon';

import 'cropperjs/dist/cropper.min.css';
import './CropperImage.css';

type CropperImageProps = {
  imageRef: MutableRefObject<HTMLImageElement | null>;
  cropperRef: MutableRefObject<Cropper | undefined>;
  onClickCancel: () => void;
  onClickDone: () => void;
};

export const CropperImage = memo(
  ({ imageRef, cropperRef, onClickCancel, onClickDone }: CropperImageProps) => {
    const { t } = useTranslation();

    const onRotate = useCallback(() => {
      cropperRef.current?.rotate(-90);
    }, [cropperRef]);

    const onRetry = useCallback(() => {
      cropperRef.current?.reset();
    }, [cropperRef]);

    const onCrop = useCallback(() => {
      cropperRef.current?.setDragMode('crop');
    }, [cropperRef]);

    const onMove = useCallback(() => {
      cropperRef.current?.setDragMode('move');
    }, [cropperRef]);

    const onEnlarge = useCallback(() => {
      cropperRef.current?.zoom(0.1);
    }, [cropperRef]);

    const onReduce = useCallback(() => {
      cropperRef.current?.zoom(-0.1);
    }, [cropperRef]);

    return (
      <div className="cropper-overlay">
        <img
          ref={(r) => {
            if (r) {
              imageRef.current = r;
            }
          }}
        />
        <div className="bottom-tool-bar">
          <button className={'tool-bar-text'} onClick={onClickCancel}>
            {t('Cancel')}
          </button>
          <button
            className="tool-bar-button"
            title={t('Rotate')}
            onClick={onRotate}
          >
            <SvgIcon
              name={SVGIconType.Rotate}
              className="tool-bar-button-icon"
            />
          </button>
          <button
            className="tool-bar-button"
            title={t('Move')}
            onClick={onMove}
          >
            <SvgIcon name={SVGIconType.Move} className="tool-bar-button-icon" />
          </button>
          <button
            className="tool-bar-button"
            title={t('Crop')}
            onClick={onCrop}
          >
            <SvgIcon name={SVGIconType.Crop} className="tool-bar-button-icon" />
          </button>
          <button
            className="tool-bar-button"
            title={t('Reset')}
            onClick={onRetry}
          >
            <SvgIcon
              name={SVGIconType.Repeat}
              className="tool-bar-button-icon"
            />
          </button>
          <button
            className="tool-bar-button"
            title={t('Zoom In')}
            onClick={onEnlarge}
          >
            <SvgIcon
              name={SVGIconType.MagnifyingPlus}
              className="tool-bar-button-icon"
            />
          </button>
          <button
            className="tool-bar-button"
            title={t('Zoom Out')}
            onClick={onReduce}
          >
            <SvgIcon
              name={SVGIconType.MagnifyingMinus}
              className="tool-bar-button-icon"
            />
          </button>
          <button className={'tool-bar-text'} onClick={onClickDone}>
            {t('Done')}
          </button>
        </div>
      </div>
    );
  },
);
